import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import StockBadge from '../components/StockBadge';
import SyncBadge from '../components/SyncBadge';
import { apiFetch } from '../utils/apiClient';
import { formatPrice } from '../utils/format';

export interface InventoryListItem {
  id: number;
  book_title: string;
  book_isbn_13: string;
  book_cover_image_url: string | null;
  vendor_name: string;
  condition: string;
  selling_price: string;
  quantity: number;
  available_quantity: number;
  shopify_sync_status: string;
  is_active: boolean;
  created_at: string;
}

const PAGE_SIZE = 20;
const BOOK_LANGUAGES = ['English', 'Sinhala', 'Tamil'];

const Inventory: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<InventoryListItem[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [stock, setStock] = useState(searchParams.get('stock') || '');
  const [language, setLanguage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(id);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, stock, language]);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (stock) params.set('stock', stock);
      if (language) params.set('language', language);
      const response = await apiFetch(`/api/inventory/?${params.toString()}`);
      if (!response.ok) throw new Error(t('common.genericError'));
      const data = await response.json();
      setItems(data.results || []);
      setCount(data.count || 0);
      setError('');
    } catch (err: any) {
      setError(err.message || t('common.genericError'));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, stock, language, t]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const hasFilters = !!(debouncedSearch || stock || language);
  const selectClass =
    'rounded-md border border-gray-300 bg-white text-sm px-3 py-2 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <Layout>
      <div className="w-full">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('inventory.title')}</h1>
          <Link
            to="/scanner"
            className="bg-blue-600 text-white px-4 py-3 sm:py-2 rounded-md active:bg-blue-700 transition-colors text-center font-medium min-h-[44px] flex items-center justify-center touch-manipulation"
          >
            {t('inventory.scanNew')}
          </Link>
        </div>

        {/* Search & filters */}
        <div className="bg-white shadow rounded-lg p-3 sm:p-4 mb-4 flex flex-col sm:flex-row gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('inventory.searchPlaceholder')}
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 min-h-[44px] text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select value={stock} onChange={(e) => setStock(e.target.value)} className={selectClass} aria-label={t('inventory.stockFilter')}>
            <option value="">{t('inventory.stockFilter')}: {t('common.all')}</option>
            <option value="in">{t('inventory.inStock')}</option>
            <option value="out">{t('inventory.outOfStock')}</option>
          </select>
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className={selectClass} aria-label={t('inventory.languageFilter')}>
            <option value="">{t('inventory.languageFilter')}: {t('common.all')}</option>
            {BOOK_LANGUAGES.map((l) => (
              <option key={l} value={l}>{t(`language.${l}`)}</option>
            ))}
          </select>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">{t('common.loading')}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 sm:p-12 text-center">
            <p className="text-gray-600 mb-4">{hasFilters ? t('inventory.noResults') : t('inventory.empty')}</p>
            {!hasFilters && (
              <Link to="/scanner" className="text-blue-600 active:text-blue-700 font-medium inline-flex items-center gap-2 py-2 min-h-[44px] touch-manipulation">
                {t('inventory.scanFirst')}
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {items.map((item) => (
                  <li key={item.id}>
                    <Link to={`/inventory/${item.id}`} className="block active:bg-gray-50 transition-colors touch-manipulation">
                      <div className="px-3 sm:px-4 py-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-12 h-16 sm:w-14 sm:h-20 bg-gray-100 rounded overflow-hidden">
                            {item.book_cover_image_url ? (
                              <img
                                src={item.book_cover_image_url}
                                alt={item.book_title}
                                className="w-full h-full object-cover"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm sm:text-base font-medium text-blue-600 truncate max-w-full">{item.book_title}</p>
                              <StockBadge available={item.available_quantity} total={item.quantity} />
                              {!item.is_active && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                  {t('item.unlisted')}
                                </span>
                              )}
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-500">
                              <span className="font-medium text-gray-800">{formatPrice(item.selling_price)}</span>
                              <span>{t('inventory.isbn')}: {item.book_isbn_13}</span>
                              <span>{t(`condition.${item.condition}`, item.condition)}</span>
                              <span>{t('inventory.vendor')}: {item.vendor_name}</span>
                            </div>
                            <div className="mt-1.5">
                              <SyncBadge status={item.shopify_sync_status} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>{t('common.showing', { count: items.length, total: count })}</span>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-2 rounded-md border border-gray-300 bg-white disabled:opacity-40 min-h-[40px]"
                  >
                    {t('common.previous')}
                  </button>
                  <span>{t('common.page', { page, total: totalPages })}</span>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-2 rounded-md border border-gray-300 bg-white disabled:opacity-40 min-h-[40px]"
                  >
                    {t('common.next')}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Inventory;
